import { Typography } from '@euler/app/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { delayed, SafeHaptics } from '@euler/utils';
import * as Clipboard from 'expo-clipboard';
import { FC, memo, useCallback } from 'react';
import { TouchableOpacity, View } from 'react-native';
import Modal from 'react-native-modal';
import QRCode from 'react-native-qrcode-svg';
import Toast from 'react-native-root-toast';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { LinkingIcon } from '../icons/LinkingIcon';
import { WechatIcon } from '../icons/WechatIcon';

interface Props {
  isVisible: boolean;
  onClose?: () => void;
  title: string;
  url: string;
  head?: string;
  iconUrl?: string;
  onShareToWechat?: () => void;
  textForCopy?: string;
}

const QrcodeSharingModal: FC<Props> = memo(props => {
  const { onClose, url, iconUrl, textForCopy, isVisible, onShareToWechat } =
    props;

  const { rpx } = useResponsiveContext();
  const { bottom } = useSafeAreaInsets();

  const onCopy = useCallback(() => {
    if (!textForCopy) return;
    void Clipboard.setStringAsync(textForCopy);
    SafeHaptics.impact();
    onClose?.();
    delayed(() => {
      Toast.show('已复制', {
        duration: 1000,
        position: Toast.positions.BOTTOM,
      });
    }, 250);
  }, [onClose, textForCopy]);

  return (
    <Modal
      isVisible={isVisible}
      backdropOpacity={0.5}
      onBackdropPress={onClose}
      style={{
        justifyContent: 'flex-end',
        margin: 0,
      }}
    >
      <View
        css={`
          background-color: white;
          justify-content: center;
          align-items: center;
          padding-bottom: ${bottom + rpx(30)}px;
        `}
      >
        <Typography.Label
          size={rpx(20)}
          regular
          css={`
            margin-top: ${rpx(20)}px;
            margin-bottom: ${rpx(15)}px;
          `}
        >
          {props.title}
        </Typography.Label>

        {Boolean(url) && (
          <QRCode
            value={url}
            logo={iconUrl ? { uri: iconUrl } : undefined}
            logoSize={rpx(30)}
            logoBackgroundColor="#ffffff"
            logoBorderRadius={rpx(5)}
            logoMargin={rpx(3)}
            size={rpx(150)}
          />
        )}

        <View
          css={`
            flex-direction: row;
            justify-content: center;
            align-items: center;
            margin-top: ${rpx(24)}px;
          `}
        >
          {onShareToWechat != null && (
            <TouchableOpacity
              onPress={onShareToWechat}
              css={`
                align-items: center;
                margin-right: ${rpx(64)}px;
              `}
            >
              <WechatIcon size={rpx(60)} />
              <Typography.Label>微信好友</Typography.Label>
            </TouchableOpacity>
          )}

          <TouchableOpacity
            onPress={onCopy}
            css={`
              align-items: center;
            `}
          >
            <LinkingIcon size={rpx(60)} />
            <Typography.Label>复制链接</Typography.Label>
          </TouchableOpacity>
        </View>
      </View>
    </Modal>
  );
});

export default QrcodeSharingModal;
